/**
 * Gestión de Contratos de Empleado
 * Fecha: 11 Nov 2025
 *
 * Vista completa para gestión de contratos con timeline, generación desde plantillas,
 * carga de PDFs firmados y acciones de activar/terminar
 */

import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { formatDate } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { contractsService } from '@/features/rh/api/contracts.service';
import { employeesService } from '@/features/rh/api/employees.service';
import type {
  Contract,
  Employee,
  ContractStatus,
} from '@/types/employee.types';
import {
  ContractStatusBadge,
  LoadingSpinner,
  Modal,
  PermissionGuard,
} from '@/shared/components';
import {
  FileText,
  CheckCircle,
  Clock4,
  CircleX,
  CirclePlus,
  Search,
  X,
  Upload,
  Trash2,
  Save,
  AlertCircle,
} from "lucide-react";
import {
  EMPLOYEE_PERMISSIONS,
} from '@/shared/hooks/usePermissions';
import SelectBuscable from '@/shared/components/SelectBuscable';
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect';
import toast from "react-hot-toast";

interface PageProps {
  isEditable?: boolean;
}

export const EmployeeContractsDetail: React.FC<PageProps> = ({
  isEditable,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const today = new Date().toISOString().split("T")[0];

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [filterContracts, setFilterContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<{
    stringFilter?: string;
    stateFilter?: string;
  }>();

  // State for Upload Contract Modal (Word)
  const [showUploadContractModal, setShowUploadContractModal] = useState(false);
  const [loadingUploadContractModal, setLoadingUploadContractModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    category: "",
    state: "",
    version: "",
    expirationDate: "",
  });

  // State for Signed Contract Modal (PDF)
  const [showSignedModal, setShowSignedModal] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    null
  );
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [isUploadingSigned, setIsUploadingSigned] = useState(false);
  const signedInputRef = useRef<HTMLInputElement>(null);

  // const canGenerate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_GENERATE);
  // const canUpload = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_CREATE);
  // const canActivate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_UPDATE);
  // const canTerminate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_DELETE);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [employeeData, contractsData] = await Promise.all([
        employeesService.getById(Number(id)),
        contractsService.getByEmployee(Number(id)),
      ]);
      setEmployee(employeeData);
      setContracts(contractsData.data);
      setFilterContracts(contractsData.data);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar contratos:", err);
      toast.error(err.message || "Error al cargar contratos");
    } finally {
      setIsLoading(false);
    }
  };

  function formatYMDToLocal(dateStr?: string) {
    if (!dateStr) return "--/--/----";
    const [y, m, d] = dateStr.split("-").map(Number);
    if (!y || !m || !d) return "--/--/----";
    return new Date(y, m - 1, d).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  const handleTriggerInput = () => {
    inputRef.current?.click();
  };

  const handleTriggerSignedInput = () => {
    signedInputRef.current?.click();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async () => {
    setLoadingUploadContractModal(true);

    // Validaciones
    if (!selectedFile) {
      setLoadingUploadContractModal(false);
      setError("Lo sentimos, el archivo del contrato es requerido");
      return;
    }
    if (!formData.category) {
      setLoadingUploadContractModal(false);
      setError("Por favor selecciona una categoría");
      return;
    }
    if (!formData.state) {
      setLoadingUploadContractModal(false);
      setError("Por favor selecciona un estado");
      return;
    }
    if (!formData.version) {
      setLoadingUploadContractModal(false);
      setError("Por favor ingresa una versión");
      return;
    }
    if (!formData.expirationDate) {
      setLoadingUploadContractModal(false);
      setError("Por favor selecciona una fecha de vencimiento");
      return;
    }

    try {
      if (employee) {
        const res = await contractsService.uploadContract(
          employee.id,
          selectedFile,
          formData
        );
        const success = typeof res === "object" && res !== null && "success" in res
          ? Boolean((res as { success?: boolean }).success)
          : true;

        if (!success) {
          throw new Error("No se pudo guardar el contrato");
        }

        onCloseModalUploadContract();
        loadData();
      }
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al subir el archivo");
    } finally {
      setLoadingUploadContractModal(false);
    }
  };

  const handleSignedUpload = async () => {
    if (!selectedContractId || !signedFile) {
      setError("Por favor selecciona un archivo PDF");
      return;
    }

    try {
      setIsUploadingSigned(true);
      await contractsService.uploadSigned(selectedContractId, signedFile);

      // Reset and reload
      setShowSignedModal(false);
      setSelectedContractId(null);
      setSignedFile(null);
      await loadData();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al subir contrato firmado:", err);
      setError(err.message || "Error al subir contrato firmado");
    } finally {
      setIsUploadingSigned(false);
    }
  };

  const openSignedModal = (contractId: number) => {
    setSelectedContractId(contractId);
    setSignedFile(null);
    setShowSignedModal(true);
  };

  const onCloseModalUploadContract = () => {
    setFormData({
      category: "",
      expirationDate: "",
      state: "",
      version: "",
    });
    setSelectedFile(null);
    setShowUploadContractModal(false);
  };

  const getContractStatusValue = (contract: Contract): string => {
    return (contract.estado || contract.status || "").toLowerCase();
  };

  const getContractsByStatus = (status: ContractStatus): Contract[] => {
    return contracts.filter((c) => getContractStatusValue(c) === status);
  };

  const activeContracts = getContractsByStatus("activo");
  const generatedContracts = getContractsByStatus("generado");
  const signedContracts = getContractsByStatus("firmado");
  const terminatedContracts = getContractsByStatus("terminado");

  const filterTableDataContracts = () => {
    let newData = [...contracts];
    if (filters?.stateFilter) {
      newData = newData.filter((doc) => {
        return getContractStatusValue(doc) === filters?.stateFilter;
      });
    }

    if (filters?.stringFilter?.trim()) {
      const term = filters.stringFilter.toLowerCase();

      newData = newData.filter((doc) => {
        const fechaCreacion = doc?.createdAt
          ? formatDate(doc.createdAt)
          : "--/--/----";
        const fechaVencimiento = doc?.vencimiento
          ? formatDate(doc.vencimiento)
          : "--/--/----";
        return (
          doc?.nombre?.toLocaleLowerCase()?.includes(term) ||
          fechaCreacion.includes(term) ||
          fechaVencimiento.includes(term)
        );
      });
    }

    setFilterContracts([...newData]);
  };

  useEffect(() => {
    filterTableDataContracts();
  }, [filters, contracts]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  if (error) {
    // CapaDeModal ya monta en el body, así que aquí sobra el createPortal que
    // este archivo hacía a mano.
    return (
      <CapaDeModal zIndex={9999} onClickFondo={() => setError(null)}>
        <div
          className="bg-white rounded-lg p-8 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-500" />
            <h3 className="text-xl font-semibold text-gray-900">Error</h3>
            <p className="text-gray-600 text-center">
              {error || "No se pudo cargar la vacante"}
            </p>
            <button
              type="button"
              onClick={() => setError(null)}
              className={estiloDeBoton('primario')}
            >
              Cerrar
            </button>
          </div>
        </div>
      </CapaDeModal>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Cargando contratos..." />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Error al cargar empleado
            </h3>
            <button
              type="button"
              onClick={() => navigate("/employees")}
              className={estiloDeBoton('primario')}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Total
              </span>
            </div>
            <p className="text-[#1F2937] text-2xl font-semibold mt-6">
              {contracts.length}
            </p>
          </div>

          <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Activos
              </span>
              <CheckCircle className="w-[24px] h-[24px] text-[#2FD599]" />
            </div>
            <p className="text-[#1F2937] text-2xl font-semibold mt-6">
              {activeContracts.length}
            </p>
          </div>

          <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                En revisión
              </span>
              <Clock4 className="w-[24px] h-[24px] text-[#EFB81E]" />
            </div>
            <p className="text-[#1F2937] text-2xl font-semibold mt-6">
              {generatedContracts.length + signedContracts.length}
            </p>
          </div>

          <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-4 max-w-full max-h-[108px]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Terminados
              </span>
              <CircleX className="w-[24px] h-[24px] text-[#EF4444]" />
            </div>
            <p className="text-[#1F2937] text-2xl font-semibold mt-3">
              {terminatedContracts.length}
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="w-full flex justify-between my-6">
          <div className="flex space-x-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar en contratos..."
                value={filters?.stringFilter}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    stringFilter: e.target.value,
                  })
                }
                className="block w-[295px] h-[40px] pl-10 pr-3 py-2 border border-[#E5E7EB] rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
              />
            </div>
            <SelectBuscable
              variante="listado"
              etiqueta="Estado"
              todos="Todos"
              opciones={[
                { value: "borrador", label: "Borrador" },
                { value: "generado", label: "Generado" },
                { value: "enviado", label: "Enviado" },
                { value: "firmado", label: "Firmado" },
                { value: "activo", label: "Activo" },
                { value: "terminado", label: "Terminado" },
                { value: "cancelado", label: "Cancelado" },
              ]}
              value={filters?.stateFilter ?? ""}
              onChange={(valor) =>
                setFilters({
                  ...filters,
                  stateFilter: valor === "" ? undefined : valor,
                })
              }
              canDelete={false}
              placeholderDeBusqueda="Buscar estado..."
            />
          </div>
          {isEditable && (
            <PermissionGuard
              permission={EMPLOYEE_PERMISSIONS.CONTRACTS_GENERATE}
            >
              <button
                type="button"
                onClick={() => setShowUploadContractModal(true)}
                className={estiloDeBoton('primario')}
              >
                <CirclePlus className="w-4 h-4" />
                Nuevo Contrato
              </button>
            </PermissionGuard>
          )}
        </div>

        {/* Contracts List */}
        {contracts.length === 0 ? (
          <div className="bg-[#ffffff] rounded-xl border border-[#E5E7EB] p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No hay contratos registrados
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Genera el primer contrato para este empleado
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Documento
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Categoría
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        Fecha de carga
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        Vencimiento
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-[#E1E1E1]">
                    {filterContracts.map((doc) => (
                      <tr
                        key={doc.id}
                        className="hover:bg-[#F4F7FB] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-[#1B2128]">
                            {doc.nombre}
                          </p>
                          {doc.urlContratoFirmado && (
                            <a
                              href={doc.urlContratoFirmado}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                            >
                              <FileText className="w-3 h-3" /> Ver firmado
                            </a>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-[#1B2128]">
                            {doc.categoria ?? "-----"}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <ContractStatusBadge
                            status={getContractStatusValue(doc)}
                          />
                        </td>

                        <td className="px-6 py-4 flex justify-center">
                          <p className="text-sm text-[#1B2128]">
                            {doc.createdAt
                              ? formatDate(doc.createdAt)
                              : "--/--/----"}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <p className="text-sm text-[#1B2128]">
                            {doc.vencimiento
                              ? formatYMDToLocal(doc.vencimiento)
                              : "--/--/----"}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Allow upload signed if not signed/terminated and user has edit rights */}
                            {isEditable && getContractStatusValue(doc) !== 'firmado' && getContractStatusValue(doc) !== 'terminado' && getContractStatusValue(doc) !== 'cancelado' && (
                              <button
                                onClick={() => openSignedModal(doc.id)}
                                title="Subir contrato firmado"
                                className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              >
                                <Upload className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Contract Modal (Word) */}
      <Modal
        isOpen={showUploadContractModal}
        onClose={onCloseModalUploadContract}
        title="Nuevo contrato"
        showCloseButton={false}
        showBorderTitle={false}
        customCloseButton={
          <button
            type="button"
            onClick={onCloseModalUploadContract}
            disabled={loadingUploadContractModal}
            className="bg-gray-600/5 w-[27px] h-[27px] flex justify-center items-center rounded-full text-gray-600/50 hover:text-gray-600 transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        }
        size="2xl"
        description={
          "Sube aquí el documento del nuevo contrato y completa la información."
        }
      >
        {/** Upload Button */}
        <div className="w-full border border-gray-300 rounded-lg p-3 flex items-center justify-between bg-white">
          {/* Label */}
          {!selectedFile ? (
            <p className="text-sm text-gray-700">{"Cargar contrato (.doc, .docx)"}</p>
          ) : (
            <div className="flex items-center">
              <FileText />
              <p className="text-sm text-gray-700 ml-2">{selectedFile?.name}</p>
            </div>
          )}

          {/* Hidden file input */}
          <input
            type="file"
            ref={inputRef}
            accept=".doc,.docx"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            className="hidden"
          />

          {/* Button */}
          {!selectedFile ? (
            <button
              type="button"
              onClick={handleTriggerInput}
              disabled={loadingUploadContractModal}
              className="flex items-center gap-1 text-[#315FB9] text-sm font-medium px-3 py-1.5 bg-[#C3C3C3]/20 rounded-md"
            >
              <Upload className="w-4 h-4" />
              {loadingUploadContractModal ? "Subiendo..." : "Cargar"}
            </button>
          ) : (
            <div>
              <Trash2
                name="Eliminar archivo"
                className="text-[#EF4444] cursor-pointer"
                onClick={() => setSelectedFile(null)}
              />
            </div>
          )}
        </div>

        {/** Form */}
        <form className="w-full flex flex-wrap justify-between my-4">
          <div className="w-1/2 pr-2">
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              Categoría
            </label>
            <SelectBuscable
              variante="formulario"
              id="category"
              name="category"
              opciones={[
                { value: "indefinido", label: "Indefinido" },
                { value: "temporal", label: "Temporal" },
                { value: "por_obra", label: "Por obra" },
                { value: "capacitacion", label: "Capacitación" },
                { value: "prueba", label: "Prueba" },
              ]}
              value={formData.category}
              onChange={(valor) => handleChange(comoEventoDeCambio("category", valor))}
              placeholder="Seleccionar..."
              placeholderDeBusqueda="Buscar..."
            />
          </div>
          <div className="w-1/2 pl-2">
            <label
              htmlFor="state"
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              Estado
            </label>
            <SelectBuscable
              variante="formulario"
              id="state"
              name="state"
              opciones={[
                { value: "borrador", label: "Borrador" },
                { value: "firmado", label: "Firmado" },
                { value: "activo", label: "Activo" },
                { value: "vencido", label: "Vencido" },
                { value: "cancelado", label: "Cancelado" },
              ]}
              value={formData.state}
              onChange={(valor) => handleChange(comoEventoDeCambio("state", valor))}
              placeholder="Seleccionar..."
              placeholderDeBusqueda="Buscar..."
            />
          </div>

          <div className="w-1/2 pr-2 mt-6">
            <label
              htmlFor="version"
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              Versión
            </label>
            <input
              id="version"
              name="version"
              required
              value={formData.version}
              onChange={handleChange}
              className="w-full border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
              placeholder="-----"
            />
          </div>
          <div className="w-1/2 pl-2 mt-6">
            <label
              htmlFor="expirationDate"
              className="block text-sm font-medium text-gray-900 mb-2"
            >
              Vencimiento
            </label>
            <input
              id="expirationDate"
              name="expirationDate"
              type="date"
              required
              min={today}
              value={formData.expirationDate}
              onChange={handleChange}
              className="w-full border-0 rounded-lg bg-[#F6F9FB] focus:bg-[#F7F9FC] focus:border-[#315FB9] focus:ring-0 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
              placeholder="-----"
            />
          </div>
        </form>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCloseModalUploadContract}
            className={estiloDeBoton('secundario')}
            disabled={loadingUploadContractModal}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => handleFileUpload()}
            className={estiloDeBoton('primario')}
            disabled={loadingUploadContractModal}
          >
            <Save className="w-4 h-4" />
            Guardar
          </button>
        </div>
      </Modal>

      {/* Upload Signed Contract Modal (PDF) */}
      <Modal
        isOpen={showSignedModal}
        onClose={() => {
          setShowSignedModal(false);
          setSignedFile(null);
        }}
        title="Subir contrato firmado"
        showCloseButton={true}
        showBorderTitle={true}
        size="md"
        description="Sube el documento PDF firmado correspondiente a este contrato."
      >
        <div className="space-y-6 py-4">
          {/* Upload Area */}
          <div className="w-full border border-gray-300 rounded-lg p-3 flex items-center justify-between bg-white">
            {!signedFile ? (
              <p className="text-sm text-gray-700">Seleccionar PDF firmado...</p>
            ) : (
              <div className="flex items-center">
                <FileText className="text-red-500" />
                <p className="text-sm text-gray-700 ml-2 truncate max-w-[200px]">{signedFile.name}</p>
              </div>
            )}

            <input
              type="file"
              ref={signedInputRef}
              accept=".pdf"
              onChange={(e) => setSignedFile(e.target.files?.[0] || null)}
              className="hidden"
            />

            {!signedFile ? (
              <button
                type="button"
                onClick={handleTriggerSignedInput}
                className="flex items-center gap-1 text-[#315FB9] text-sm font-medium px-3 py-1.5 bg-[#C3C3C3]/20 rounded-md"
              >
                <Upload className="w-4 h-4" />
                Elegir
              </button>
            ) : (
              <Trash2
                className="text-[#EF4444] cursor-pointer w-5 h-5"
                onClick={() => setSignedFile(null)}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setShowSignedModal(false)}
              disabled={isUploadingSigned}
              className={estiloDeBoton('secundario')}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSignedUpload}
              disabled={isUploadingSigned || !signedFile}
              className={estiloDeBoton('primario')}
            >
              {isUploadingSigned ? <LoadingSpinner size="sm" /> : <Upload className="w-4 h-4" />}
              {isUploadingSigned ? "Subiendo..." : "Subir Firmado"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
